import { ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'

import { AppShell } from '@/components/px/AppShell'
import { DensityNote } from '@/components/px/DensityNote'
import { KpiCard } from '@/components/px/KpiCard'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill } from '@/components/px/StatusPill'
import { libraryNav } from '@/components/px/nav'
import { ConceptBadge } from '@/components/px/ConceptBadge'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  ANCHORS,
  DECISIONS_TABLE,
  INHERITANCE,
  PAGE,
  PRE_LOADED,
  REQUIRED_DECISIONS,
  RULES,
  RULES_TABLE,
  SECTIONS,
  SECTIONS_TABLE,
  WHAT_CHANGED,
  densityNote,
  kpis,
  type Fragment,
} from '@/demo/content/blueprintConfig'
import { cn } from '@/lib/utils'

/**
 * Surface 2.9 — Blueprint configuration. The blueprint is shown as what it is:
 * a structure the group configures once per vertical and versions centrally —
 * the sections a retail blueprint always contains, the rules that decide
 * standard from custom, and the decisions it raises to the client on its own.
 *
 * Every string, figure and href comes from `@/demo/content/blueprintConfig`,
 * which in turn reads the pack, the hero project's live decisions and the
 * portfolio's reuse rate rather than re-stating them.
 */

/* ── Prose in which some words are real destinations ────────────────────── */

function Prose({ fragments }: { fragments: readonly Fragment[] }) {
  return (
    <>
      {fragments.map((fragment, index) =>
        fragment.to === undefined ? (
          <span key={index}>{fragment.text}</span>
        ) : (
          <Link
            key={index}
            to={fragment.to}
            className="font-medium text-foreground underline underline-offset-2 hover:no-underline"
          >
            {fragment.text}
          </Link>
        )
      )}
    </>
  )
}

/** A stated block: small-caps label over its content. */
function Panel({
  label,
  id,
  mint = false,
  className,
  children,
}: {
  label: string
  id?: string
  mint?: boolean
  className?: string
  children: React.ReactNode
}) {
  return (
    <section
      id={id}
      aria-label={label}
      className={cn(
        'rounded-lg border border-border px-4 py-3',
        mint ? 'bg-accent' : 'bg-card',
        className
      )}
    >
      <SectionLabel className={mint ? 'text-accent-foreground' : undefined}>{label}</SectionLabel>
      <div className="mt-2">{children}</div>
    </section>
  )
}

const HEAD_CLASS = 'text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground'
const ROW_HEAD_CLASS = 'h-auto whitespace-nowrap py-2.5 align-top text-sm font-medium'
const CELL_CLASS = 'whitespace-normal py-2.5 align-top text-sm'

/* ── 1 · Sections ───────────────────────────────────────────────────────── */

function SectionsTable() {
  return (
    <section id={ANCHORS.sections} aria-label={SECTIONS_TABLE.label} className="mt-6">
      <SectionLabel>{SECTIONS_TABLE.label}</SectionLabel>
      <Table className="mt-2">
        <TableHeader>
          <TableRow>
            <TableHead scope="col" className={cn(HEAD_CLASS, 'w-10')}>
              {SECTIONS_TABLE.columns.index}
            </TableHead>
            <TableHead scope="col" className={HEAD_CLASS}>
              {SECTIONS_TABLE.columns.section}
            </TableHead>
            <TableHead scope="col" className={HEAD_CLASS}>
              {SECTIONS_TABLE.columns.filledBy}
            </TableHead>
            <TableHead scope="col" className={HEAD_CLASS}>
              {SECTIONS_TABLE.columns.required}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {SECTIONS.map((section) => (
            <TableRow key={section.index}>
              <TableCell className={cn(CELL_CLASS, 'v-figure font-mono tabular-nums text-muted-foreground')}>
                {section.index}
              </TableCell>
              <TableHead scope="row" className={cn(ROW_HEAD_CLASS, 'max-w-[26rem] whitespace-normal')}>
                <span className="text-foreground">{section.name}</span>
                <span className="mt-0.5 block text-xs font-normal text-muted-foreground">{section.note}</span>
              </TableHead>
              <TableCell className={cn(CELL_CLASS, 'text-ink-soft')}>
                <Prose fragments={section.filledBy} />
              </TableCell>
              <TableCell className={CELL_CLASS}>
                {section.required ? (
                  <StatusPill tone="decided">{SECTIONS_TABLE.requiredLabel}</StatusPill>
                ) : (
                  <StatusPill tone="neutral">{SECTIONS_TABLE.conditionalLabel}</StatusPill>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <p className="mt-3 max-w-prose text-sm leading-relaxed text-ink-soft">
        <Prose fragments={SECTIONS_TABLE.gateNote} />
      </p>
    </section>
  )
}

/* ── 2 · Standard vs custom ─────────────────────────────────────────────── */

function RulesTable() {
  return (
    <section id={ANCHORS.rules} aria-label={RULES_TABLE.label} className="mt-8">
      <SectionLabel>{RULES_TABLE.label}</SectionLabel>
      <Table className="mt-2">
        <TableHeader>
          <TableRow>
            <TableHead scope="col" className={cn(HEAD_CLASS, 'w-[26rem]')}>
              {RULES_TABLE.columns.rule}
            </TableHead>
            <TableHead scope="col" className={HEAD_CLASS}>
              {RULES_TABLE.columns.effect}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {RULES.map((rule) => (
            <TableRow key={rule.key}>
              <TableHead scope="row" className={cn(ROW_HEAD_CLASS, 'whitespace-normal text-foreground')}>
                <Prose fragments={rule.rule} />
              </TableHead>
              <TableCell className={cn(CELL_CLASS, 'text-ink-soft')}>
                <span className="inline-flex max-w-prose items-baseline gap-2">
                  <ArrowRight className="h-3.5 w-3.5 shrink-0 translate-y-0.5 text-muted-foreground" aria-hidden />
                  <span>
                    <Prose fragments={rule.effect} />
                  </span>
                </span>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <p className="mt-3 max-w-prose text-sm leading-relaxed text-muted-foreground">{RULES_TABLE.caption}</p>
    </section>
  )
}

/* ── 3 · Required decisions ─────────────────────────────────────────────── */

function DecisionsTable() {
  return (
    <section id={ANCHORS.decisions} aria-label={DECISIONS_TABLE.label} className="mt-8">
      <SectionLabel>{DECISIONS_TABLE.label}</SectionLabel>
      <Table className="mt-2">
        <TableHeader>
          <TableRow>
            <TableHead scope="col" className={cn(HEAD_CLASS, 'w-16')}>
              {DECISIONS_TABLE.columns.id}
            </TableHead>
            <TableHead scope="col" className={HEAD_CLASS}>
              {DECISIONS_TABLE.columns.decision}
            </TableHead>
            <TableHead scope="col" className={HEAD_CLASS}>
              {DECISIONS_TABLE.columns.owner}
            </TableHead>
            <TableHead scope="col" className={HEAD_CLASS}>
              {DECISIONS_TABLE.columns.cost}{' '}
              <span className="font-normal normal-case tracking-normal">({DECISIONS_TABLE.costNote})</span>
            </TableHead>
            <TableHead scope="col" className={HEAD_CLASS}>
              {DECISIONS_TABLE.columns.ageing}
            </TableHead>
            <TableHead scope="col" className={HEAD_CLASS}>
              {DECISIONS_TABLE.columns.state}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {REQUIRED_DECISIONS.map((decision) => (
            <TableRow key={decision.id} className={cn(decision.live !== null && 'bg-muted')}>
              <TableHead scope="row" className={cn(ROW_HEAD_CLASS, 'v-figure font-mono text-foreground')}>
                {decision.id}
              </TableHead>
              <TableCell className={cn(CELL_CLASS, 'max-w-[28rem]')}>
                <span className="font-medium text-foreground">{decision.title}</span>
                <span className="mt-0.5 block text-xs text-muted-foreground">{decision.question}</span>
              </TableCell>
              <TableCell className={cn(CELL_CLASS, 'text-ink-soft')}>{decision.owner}</TableCell>
              <TableCell className={cn(CELL_CLASS, 'v-figure font-mono tabular-nums text-foreground')}>
                {decision.costRange}
              </TableCell>
              <TableCell className={cn(CELL_CLASS, 'max-w-[18rem] text-xs text-muted-foreground')}>
                {decision.ageing}
              </TableCell>
              <TableCell className={CELL_CLASS}>
                {decision.live === null ? (
                  <>
                    <span aria-hidden className="text-muted-foreground">
                      {DECISIONS_TABLE.notLiveLabel}
                    </span>
                    <span className="sr-only">{DECISIONS_TABLE.notLiveHint}</span>
                  </>
                ) : (
                  <Link to={decision.live.href} className="underline-offset-2 hover:underline">
                    <StatusPill tone="review">{decision.live.label}</StatusPill>
                  </Link>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <p className="mt-3 max-w-prose text-sm leading-relaxed text-muted-foreground">
        <Prose fragments={DECISIONS_TABLE.caption} />
      </p>
    </section>
  )
}

/* ── 4 · Panels ─────────────────────────────────────────────────────────── */

function Panels() {
  return (
    <div className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-3">
      <Panel label={PRE_LOADED.label}>
        <p className="text-sm leading-relaxed text-foreground">
          <Prose fragments={PRE_LOADED.lead} />
        </p>
        <dl className="mt-3">
          {PRE_LOADED.assets.map((asset) => (
            <div key={asset.key} className="v-row flex items-baseline justify-between gap-3 py-1.5">
              <dt className="text-sm text-ink-soft">{asset.name}</dt>
              <dd className="text-xs text-muted-foreground">{asset.phase}</dd>
            </div>
          ))}
        </dl>
      </Panel>

      <Panel label={INHERITANCE.label}>
        <p className="text-sm leading-relaxed text-foreground">{INHERITANCE.lead}</p>
        <ol className="mt-3 space-y-2">
          {INHERITANCE.layers.map((layer, index) => (
            <li key={layer.key} className="flex items-baseline gap-2">
              <span className="v-figure w-4 shrink-0 font-mono text-xs tabular-nums text-muted-foreground">
                {index + 1}
              </span>
              <span>
                {layer.href === undefined ? (
                  <Badge variant="outline">{layer.label}</Badge>
                ) : (
                  <Badge variant="outline" asChild>
                    <Link to={layer.href} className="underline-offset-2 hover:underline">
                      {layer.label}
                    </Link>
                  </Badge>
                )}
                <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">{layer.detail}</span>
              </span>
            </li>
          ))}
        </ol>
        <Separator className="my-3" />
        <p className="text-sm font-medium leading-relaxed text-foreground">{INHERITANCE.rule}</p>
      </Panel>

      <Panel label={WHAT_CHANGED.label} mint>
        <p className="text-sm leading-relaxed text-accent-foreground">{WHAT_CHANGED.lead}</p>
        <ul className="mt-3 space-y-2.5">
          {WHAT_CHANGED.changes.map((change) => (
            <li key={change.key}>
              <p className="text-sm leading-relaxed text-foreground">{change.change}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {WHAT_CHANGED.fromLabel}{' '}
                <Link
                  to={change.href}
                  className="font-medium text-foreground underline underline-offset-2 hover:no-underline"
                >
                  {change.source}
                </Link>{' '}
                · {change.evidence}
              </p>
            </li>
          ))}
        </ul>
        <p className="mt-3 text-xs leading-relaxed text-accent-foreground">
          <Prose fragments={WHAT_CHANGED.closing} />
        </p>
      </Panel>
    </div>
  )
}

/* ── The surface ────────────────────────────────────────────────────────── */

export default function BlueprintConfigPage() {
  return (
    <AppShell
      moduleLabel={PAGE.moduleLabel}
      breadcrumb={PAGE.breadcrumb}
      contextPill={PAGE.contextPill}
      nav={libraryNav}
    >
      {/* PX-W6.6 (AC-1.6): this surface renders demo content and had no
          badge. Never hide a surface — badge it. */}
      <div className="mb-3">
        <ConceptBadge />
      </div>
      <header>
        <h1 className="font-serif text-2xl font-semibold text-foreground">{PAGE.title}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          <Prose fragments={PAGE.meta} />
        </p>
        <p className="mt-3 max-w-prose text-sm leading-relaxed text-ink-soft">{PAGE.standfirst}</p>
      </header>

      <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
        {kpis().map((kpi) => (
          <KpiCard key={kpi.key} label={kpi.label} figure={kpi.figure} qualifier={kpi.qualifier} />
        ))}
      </div>

      <SectionsTable />
      <RulesTable />
      <DecisionsTable />
      <Panels />

      <DensityNote>
        <Prose fragments={densityNote()} />
      </DensityNote>
    </AppShell>
  )
}
